import { IsoDate, IsoDateTime, IsoDateTimeTz } from '../../../types/iso-date';
import { deconstructDate } from '../../helper/deconstruct-date.function';
import { constructDate } from '../../helper/construct-date.function';
import { shiftDateUtc } from '../../helper/shift-date-utc.function';


/**
 * Returns the end of the week for the given ISO input.
 * - For date-only (YYYY-MM-DD) -> returns 'YYYY-MM-DD' of week end
 * - For date-time without tz -> returns 'YYYY-MM-DDT23:59:59.999'
 * - For date-time with tz -> returns 'YYYY-MM-DDT23:59:59.999Z|±HH:MM'
 *
 * Week start defaults to Monday (end = Sunday). For locale 'en-US', start = Sunday (end = Saturday).
 */
export function endOfWeek<T extends IsoDate | IsoDateTime | IsoDateTimeTz>(input: T, locale?: string): T {
  const weekStartsOn: 0 | 1 = (locale || '').toLowerCase() === 'en-us' ? 0 : 1; // 0=Sun,1=Mon
  const weekEndsOn = (weekStartsOn + 6) % 7; // 6 days after start

  const deconstructed = deconstructDate(input);
  if (deconstructed._type !== 'date' && deconstructed._type !== 'dateTime' && deconstructed._type !== 'dateTimeTz') {
    throw new Error('endOfWeek: invalid ISO date/time string');
  }

  const dow = new Date(Date.UTC(deconstructed.year, deconstructed.month - 1, deconstructed.day)).getUTCDay();
  const delta = (weekEndsOn - dow + 7) % 7;
  const end = shiftDateUtc(deconstructed.year, deconstructed.month, deconstructed.day, delta);
  deconstructed.year = end.year;
  deconstructed.month = end.month;
  deconstructed.day = end.day;

  if (deconstructed._type !== 'date') {
    deconstructed.hours = 23;
    deconstructed.minutes = 59;
    deconstructed.seconds = 59;
    deconstructed.milliseconds = 999;
  }

  return constructDate(deconstructed) as unknown as T;
}

